// FILE: src/admin/pages/support/tabs/SupportNotesTab.jsx
// (UNCHANGED from what you pasted — works once adminSupportApi NOTES funcs are fixed)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  listAdminSupportNotes,
  createAdminSupportNote,
  toggleAdminSupportNotePin,
  deleteAdminSupportNote,
} from "../../../../api/adminSupportApi";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function safeStr(x) {
  return x == null ? "" : String(x).trim();
}
function up(x) {
  return safeStr(x).toUpperCase();
}

function timeAgo(date) {
  const ms = Date.now() - date.getTime();
  if (!Number.isFinite(ms)) return "";
  const sec = Math.floor(ms / 1000);
  if (sec < 30) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mon = Math.floor(day / 30);
  if (mon < 12) return `${mon} month${mon === 1 ? "" : "s"} ago`;
  const yr = Math.floor(mon / 12);
  return `${yr} year${yr === 1 ? "" : "s"} ago`;
}

function fmtDT(x) {
  if (!x) return { pretty: "—", rel: "" };
  const d = new Date(x);
  if (Number.isNaN(d.getTime())) return { pretty: String(x), rel: "" };
  const pretty = new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
  return { pretty, rel: timeAgo(d) };
}

function copyText(x) {
  const v = String(x || "");
  if (!v) return;
  try {
    navigator.clipboard?.writeText(v);
  } catch {}
}

function getAdminIdBestEffort() {
  try {
    const direct = localStorage.getItem("adminId");
    if (direct && String(direct).trim()) return direct;

    const raw =
      localStorage.getItem("admin_session") ||
      localStorage.getItem("adminSession") ||
      localStorage.getItem("ADMIN_SESSION") ||
      localStorage.getItem("session_admin");

    if (!raw) return undefined;
    const obj = JSON.parse(raw);
    return obj?.adminId ?? obj?.id ?? obj?.userId ?? obj?.user?.id ?? undefined;
  } catch {
    return undefined;
  }
}

/** Backend may return envelope {ok, data}; keep best-effort */
function unwrapApi(x) {
  if (x && typeof x === "object" && "ok" in x && "data" in x) return x.data;
  return x;
}

/* --------------------- UI atoms --------------------- */
function Tag({ label, tone = "neutral", className = "" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <span
      className={classNames(
        "inline-flex items-center border px-2 py-1 text-[11px] font-bold uppercase tracking-wider leading-none rounded-md",
        cls,
        className
      )}
    >
      {label}
    </span>
  );
}

function Button({ children, onClick, disabled, tone = "soft", className = "", title, type = "button" }) {
  const cls =
    tone === "danger"
      ? "border-rose-700 bg-rose-700 text-white hover:opacity-95"
      : tone === "dark"
      ? "border-slate-900 bg-slate-900 text-white hover:opacity-95"
      : tone === "ghost"
      ? "border-transparent bg-transparent text-slate-700 hover:bg-slate-50"
      : "border-slate-200 bg-white text-slate-900 hover:bg-slate-50";

  return (
    <button
      type={type}
      title={title}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={classNames(
        "inline-flex h-9 items-center justify-center gap-2 border px-3 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed rounded-md",
        cls,
        className
      )}
    >
      {children}
    </button>
  );
}

function SelectField({ value, onChange, children, className = "", title }) {
  return (
    <div className={classNames("relative min-w-0", className)} title={title}>
      <select
        value={value}
        onChange={onChange}
        className={classNames(
          "h-10 w-full appearance-none border bg-white pl-3 pr-10 text-sm font-semibold text-slate-900 outline-none",
          "rounded-md border-slate-200 hover:bg-slate-50 focus:border-slate-300",
          "truncate"
        )}
      >
        {children}
      </select>
      <svg
        viewBox="0 0 24 24"
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
        fill="none"
        aria-hidden="true"
      >
        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function Section({ title, subtitle, right, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">{title}</div>
          {subtitle ? <div className="mt-1 text-[13px] font-medium text-slate-500">{subtitle}</div> : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function IconSearch({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M10.5 18.5a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z" stroke="currentColor" strokeWidth="2" />
      <path d="M16.5 16.5 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconX({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconPin({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M14 9V4l1-1h-6l1 1v5L7 12v2h10v-2l-3-3ZM12 14v7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* --------------------- helpers --------------------- */
const NOTE_CATEGORIES = ["ALL", "GENERAL", "PAYMENT", "REFUND", "OPS", "FRAUD", "TECH"];

function categoryTone(cat) {
  const c = up(cat);
  if (c === "FRAUD") return "bad";
  if (c === "REFUND" || c === "PAYMENT") return "warn";
  if (c === "OPS" || c === "TECH") return "neutral";
  return "neutral";
}

function normalizeListPayload(api) {
  const data = unwrapApi(api) || {};
  const items = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data?.content)
    ? data.content
    : Array.isArray(data)
    ? data
    : [];
  const page =
    data?.page ||
    {
      page: Number(data?.number ?? 0),
      size: Number(data?.size ?? 25),
      totalElements: Number(data?.totalElements ?? items.length ?? 0),
      totalPages: Number(data?.totalPages ?? 1),
      hasNext: !!data?.hasNext,
      hasPrev: !!data?.hasPrev,
    };
  return { items, page };
}

function rowMatchesNote(n, q) {
  const qq = safeStr(q);
  if (!qq) return true;
  const needle = up(qq);

  const id = n?.id ?? "";
  const category = n?.category ?? "";
  const text = n?.text ?? n?.note ?? n?.message ?? "";
  const createdBy = n?.createdBy ?? n?.createdByAdminId ?? n?.adminId ?? "";
  const refs = Array.isArray(n?.refs) ? n.refs.join(" ") : n?.refsJson ?? n?.refs_text ?? "";

  const hay = [id, category, text, createdBy, refs].map((x) => up(x)).join(" | ");
  return hay.includes(needle);
}

export default function SupportNotesTab() {
  const { entityType = "users", entityId } = useParams();
  const enabled = !!entityId && ["users", "owners", "drivers"].includes(String(entityType));

  const adminId = useMemo(() => getAdminIdBestEffort(), []);

  // List controls
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("ALL");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  // List data
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [res, setRes] = useState({
    items: [],
    page: { page: 0, size: 25, totalElements: 0, totalPages: 1, hasNext: false, hasPrev: false },
  });

  // Composer
  const [text, setText] = useState("");
  const [newCategory, setNewCategory] = useState("GENERAL");
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);

  // Prevent double-fetch on reset
  const suppressAutoFetchRef = useRef(false);

  async function fetchNotes({ nextPage = page, nextSize = size, nextQ = q, nextCategory = category } = {}) {
    if (!enabled) return;

    setLoading(true);
    setErr("");

    try {
      const api = await listAdminSupportNotes({
        entityType,
        entityId,
        q: (nextQ || "").trim() || undefined,
        category: nextCategory === "ALL" ? undefined : nextCategory,
        page: nextPage,
        size: nextSize,
        adminId,
      });

      const { items, page: meta } = normalizeListPayload(api);
      setRes({ items, page: meta });
    } catch (e) {
      setErr(e?.message || "Failed to load notes");
      setRes({
        items: [],
        page: { page: 0, size: nextSize, totalElements: 0, totalPages: 1, hasNext: false, hasPrev: false },
      });
    } finally {
      setLoading(false);
    }
  }

  async function saveNote() {
    const body = safeStr(text);
    if (!body) return;

    setSaving(true);
    setErr("");

    try {
      await createAdminSupportNote({
        entityType,
        entityId,
        text: body,
        category: newCategory,
        pinned: !!pinned,
        adminId,
      });

      setText("");
      setPinned(false);
      setNewCategory("GENERAL");
      setPage(0);
      await fetchNotes({ nextPage: 0, nextQ: q, nextCategory: category, nextSize: size });
    } catch (e) {
      setErr(e?.message || "Failed to save note");
    } finally {
      setSaving(false);
    }
  }

  async function togglePin(note) {
    if (!note?.id) return;
    setErr("");
    try {
      await toggleAdminSupportNotePin(note.id, { pinned: !note.pinned, adminId });
      await fetchNotes();
    } catch (e) {
      setErr(e?.message || "Failed to update pin");
    }
  }

  async function removeNote(note) {
    if (!note?.id) return;
    setErr("");
    try {
      await deleteAdminSupportNote(note.id, { adminId });
      await fetchNotes();
    } catch (e) {
      setErr(e?.message || "Failed to delete note");
    }
  }

  // Initial reset on entity change
  useEffect(() => {
    if (!enabled) return;

    suppressAutoFetchRef.current = true;
    setQ("");
    setCategory("ALL");
    setPage(0);
    setSize(25);

    fetchNotes({ nextPage: 0, nextQ: "", nextCategory: "ALL", nextSize: 25 }).finally(() => {
      suppressAutoFetchRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, entityType, entityId]);

  // Fetch when paging/filter changes
  useEffect(() => {
    if (!enabled) return;
    if (suppressAutoFetchRef.current) return;
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, page, size, category]);

  // Debounced q
  useEffect(() => {
    if (!enabled) return;
    const t = setTimeout(() => {
      const nextQ = q;
      if (page !== 0) setPage(0);
      else fetchNotes({ nextPage: 0, nextQ });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const rows = res.items || [];
  const meta = res.page || {};
  const total = Number(meta.totalElements ?? rows.length ?? 0);
  const totalPages = Math.max(1, Number(meta.totalPages ?? 1));
  const safePage = Math.min(Math.max(0, page), totalPages - 1);

  // Client-side match as safety
  const filteredRows = useMemo(() => {
    const base = rows.filter((n) => rowMatchesNote(n, q));
    return base.sort((a, b) => Number(!!b?.pinned) - Number(!!a?.pinned));
  }, [rows, q]);

  const canSave = !!safeStr(text) && !saving;

  if (!enabled) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="text-sm font-extrabold text-slate-900">Coming soon</div>
        <div className="mt-1 text-[13px] font-medium text-slate-500">Notes can be enabled for Users / Owners / Drivers.</div>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-4">
      <Section
        title="Notes"
        subtitle="Internal support notes (visible to admins only). Search by any text or IDs inside notes."
        right={
          <div className="flex items-center gap-2">
            <Button tone="soft" disabled={loading} onClick={() => fetchNotes({ nextPage: safePage, nextSize: size })}>
              Refresh
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-center">
            {/* Search */}
            <div className="min-w-0 lg:col-span-5">
              <div className="flex h-10 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 focus-within:border-slate-300">
                <IconSearch className="h-4 w-4 shrink-0 text-slate-500" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search notes: text / bookingId / requestId / paymentId / traceId ..."
                  className="min-w-0 w-full bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
                />
                {q ? (
                  <button
                    onClick={() => setQ("")}
                    className="shrink-0 rounded-md border border-slate-200 bg-white p-1 text-slate-600 hover:bg-slate-50"
                    title="Clear"
                    type="button"
                  >
                    <IconX className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>

            {/* Category filter */}
            <div className="min-w-0 lg:col-span-3">
              <SelectField
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setPage(0);
                }}
                title="Filter by category"
              >
                {NOTE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c === "ALL" ? "All categories" : c}
                  </option>
                ))}
              </SelectField>
            </div>

            {/* Page size */}
            <div className="min-w-0 lg:col-span-2">
              <SelectField
                value={String(size)}
                onChange={(e) => {
                  setSize(Number(e.target.value));
                  setPage(0);
                }}
                title="Rows per page"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}/page
                  </option>
                ))}
              </SelectField>
            </div>

            <div className="min-w-0 lg:col-span-2 text-right text-[13px] font-medium text-slate-500">
              {loading ? "Loading…" : `${filteredRows.length} shown • ${total} total`}
            </div>
          </div>

          {err ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
              {err}
            </div>
          ) : null}

          {/* Composer */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <Tag label="NEW NOTE" />
                <SelectField
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  title="New note category"
                  className="w-[200px]"
                >
                  {NOTE_CATEGORIES.filter((x) => x !== "ALL").map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </SelectField>

                <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={pinned}
                    onChange={(e) => setPinned(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Pin
                </label>
              </div>

              <div className="text-[12px] font-medium text-slate-500">Avoid sensitive data (card/OTP). Keep it support-relevant.</div>
            </div>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write an internal support note… (include ids like bookingId/paymentId/traceId if needed)"
              className="mt-3 min-h-[110px] w-full resize-none rounded-md border border-slate-200 bg-white p-3 text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-300"
            />

            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-[12px] font-medium text-slate-500">{safeStr(text).length}/2000</div>
              <div className="flex items-center gap-2">
                <Button tone="soft" disabled={saving} onClick={() => setText("")}>
                  Clear
                </Button>
                <Button tone="dark" disabled={!canSave} onClick={saveNote}>
                  {saving ? "Saving…" : "Save note"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Notes list */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-3">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Notes timeline</div>
        </div>

        {loading ? (
          <div className="px-5 py-10 text-sm font-semibold text-slate-600">Loading notes…</div>
        ) : filteredRows.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="text-sm font-semibold text-slate-900">No notes found</div>
            <div className="mt-1 text-[13px] font-medium text-slate-500">Add the first note for this user.</div>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {filteredRows.map((n) => {
              const id = n?.id;
              const cat = up(n?.category || "GENERAL");
              const isPinned = !!n?.pinned;
              const created = fmtDT(n?.createdAt || n?.created_at).pretty;
              const rel = fmtDT(n?.createdAt || n?.created_at).rel;

              const createdBy = n?.createdByName || n?.createdBy || n?.createdByAdminId || n?.adminId || "—";
              const textVal = n?.text ?? n?.note ?? n?.message ?? "";

              return (
                <div key={String(id ?? Math.random())} className="px-5 py-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Tag label={cat} tone={categoryTone(cat)} />
                        {isPinned ? (
                          <span className="inline-flex items-center gap-1 text-[12px] font-extrabold text-slate-900">
                            <IconPin className="h-4 w-4" /> PINNED
                          </span>
                        ) : null}
                        {id != null ? <span className="text-[12px] font-semibold text-slate-400 tabular-nums">#{id}</span> : null}
                      </div>

                      <div className="mt-2 whitespace-pre-wrap break-words text-[14px] font-semibold text-slate-900">
                        {textVal || "—"}
                      </div>

                      <div className="mt-2 text-[12px] font-medium text-slate-500">
                        By <span className="font-semibold text-slate-900">{createdBy}</span> • {created}
                        {rel ? ` (${rel})` : ""}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Button tone="soft" onClick={() => copyText(textVal)} title="Copy note text">
                        Copy
                      </Button>
                      <Button tone="soft" onClick={() => togglePin(n)} title={isPinned ? "Unpin note" : "Pin note"}>
                        {isPinned ? "Unpin" : "Pin"}
                      </Button>
                      <Button tone="danger" onClick={() => removeNote(n)} title="Delete note (soft delete recommended)">
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        <div className="flex flex-col gap-2 border-t border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-[13px] font-medium text-slate-500">
            Page <span className="font-semibold text-slate-900">{safePage + 1}</span> of{" "}
            <span className="font-semibold text-slate-900">{totalPages}</span>
          </div>

          <div className="flex gap-2">
            <Button disabled={loading || safePage <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))} tone="soft">
              Prev
            </Button>
            <Button disabled={loading || safePage >= totalPages - 1} onClick={() => setPage((p) => p + 1)} tone="soft">
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
